/**
 * GpsMapaCobradores — mapa GPS con OpenStreetMap (Leaflet, gratuito, SIN key).
 * Reemplaza el mapa de Google Maps: pinta siempre (no depende de una API key).
 *
 * Puntos:
 *  - Los del mapa de la RUTA (gps.cs — 4 colores: verde=Pagó, azul=Atraso,
 *    rojo=Por Cobrar, naranja=Pagó Atraso).
 *  - GPS COBRADORES (gpsrutascobradores.cs): el último cobro del día por zona.
 *  - TRACKING (nuevo): la posición ACTUAL de cada cobrador (el switch del
 *    móvil reporta cada 2 min — el marcador del cobrador con su hora).
 */

import React, { useRef } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Punto del mapa "GPS COBRADORES" — réplica de gpsrutascobradores.cs:692-713:
// popup = titulo (ClienteNombre), descripcion (ClienteNegocio), linkDsc
// (AbonoValor "$ Z,ZZZ,ZZZ,ZZ9.99"), street (AbonoHora), link (recibo,
// geturl.cs:84 → ruta web /abonos/:nro).
export interface GpsCobradorPunto {
  zonaCodigo: number
  zonaNombre: string
  lat: string
  lng: string
  titulo: string
  descripcion: string
  linkDsc: string
  street: string
  abonoNro?: number
  link?: string
  // Color del marcador (gps.cs:741 — mapa de la RUTA usa 4 colores:
  // verde=Pagó, azul=Atraso, rojo=Por Cobrar, naranja=Pagó Atraso).
  color?: string
  // [RGM] TRACKING: la posición actual del cobrador de la zona (el reporte
  // del móvil). null si el cobrador no tiene el tracking activo.
  tracking?: { lat: number; lng: number; hora: string } | null
}

// Colores de los puntos (los mismos del GX; el icono es un círculo CSS — sin
// las imágenes de Google Maps).
const COLOR_HEX: Record<string, string> = {
  verde: '#1E9E6A',
  azul: '#0d6efd',
  rojo: '#D64545',
  naranja: '#F59E0B',
}

const dotIcon = (color: string) =>
  L.divIcon({
    className: '',
    html: `<div style="width:16px;height:16px;border-radius:50%;background:${COLOR_HEX[color] ?? COLOR_HEX.verde};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  })

const cobradorIcon = () =>
  L.divIcon({
    className: '',
    html: `<div style="width:22px;height:22px;border-radius:50%;background:#078BCD;border:3px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;color:#fff;font-size:11px;font-weight:700">C</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  })

interface GpsMapaCobradoresProps {
  puntos: GpsCobradorPunto[]
}

const GpsMapaCobradores: React.FC<GpsMapaCobradoresProps> = ({ puntos }) => {
  const validos = puntos.filter(
    (p) => p.lat && p.lng && !Number.isNaN(Number(p.lat)) && !Number.isNaN(Number(p.lng)),
  )
  const conTracking = validos.filter((p) => p.tracking)
  // Leaflet dentro de un modal/panel: al montar puede medir 0 (el modal aún
  // anima) → los tiles no cargan. invalidateSize tras el primer render lo
  // recalcula (el mapa pinta siempre, también en el modal GPS de la fila).
  const mapRef = useRef<L.Map | null>(null)
  const centro: [number, number] = conTracking.length
    ? [conTracking[0].tracking!.lat, conTracking[0].tracking!.lng]
    : validos.length
      ? [Number(validos[0].lat), Number(validos[0].lng)]
      : [4.6097, -74.0817] // Bogotá (fallback)

  return (
    <div
      style={{ height: 420, borderRadius: 8, overflow: 'hidden', border: '1px solid #dee2e6' }}
      aria-label="Mapa GPS de cobradores"
    >
      <MapContainer
        center={centro}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
        whenReady={() => {
          setTimeout(() => mapRef.current?.invalidateSize(), 200)
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {validos.map((p, i) => (
          <Marker key={`p-${i}`} position={[Number(p.lat), Number(p.lng)]} icon={dotIcon(p.color ?? 'verde')}>
            <Popup>
              <div style={{ fontSize: 12 }}>
                <strong>{p.titulo}</strong>
                {p.descripcion ? <div>{p.descripcion}</div> : null}
                {p.linkDsc ? <div>{p.linkDsc}</div> : null}
                {p.street ? <div>{p.street}</div> : null}
                {p.link ? (
                  <a href={p.link} target="_blank" rel="noreferrer">Ver recibo</a>
                ) : null}
              </div>
            </Popup>
          </Marker>
        ))}
        {/* TRACKING: la posición ACTUAL de cada cobrador (el reporte del móvil). */}
        {conTracking.map((p, i) => (
          <Marker
            key={`t-${i}`}
            position={[p.tracking!.lat, p.tracking!.lng]}
            icon={cobradorIcon()}
          >
            <Popup>
              <div style={{ fontSize: 12 }}>
                <strong>Cobrador — Ruta {p.zonaNombre}</strong>
                <div>Último reporte: {p.tracking!.hora}</div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  )
}

export default GpsMapaCobradores
